import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "mt.cart.v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) { /* ignore */ }
  }, [items]);

  const addItem = useCallback((product) => {
    setItems((cur) => {
      const existing = cur.find((i) => i.id === product.id && i.size === product.size);
      if (existing) return cur.map((i) => (i.id === product.id && i.size === product.size) ? { ...i, qty: i.qty + 1 } : i);
      return [...cur, {
        id: product.id,
        name: product.name,
        price: product.sale_price || product.price,
        original_price: product.price,
        category: product.category,
        image: product.image || (product.images && product.images[0]) || null,
        size: product.size || null,
        qty: 1,
      }];
    });
  }, []);

  const updateQty = useCallback((id, delta, size = null) => {
    setItems((cur) => cur
      .map((i) => (i.id === id && i.size === size) ? { ...i, qty: i.qty + delta } : i)
      .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((id, size = null) => {
    setItems((cur) => cur.filter((i) => !(i.id === id && i.size === size)));
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => setOpen(false), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);

  return (
    <CartContext.Provider value={{ items, count, subtotal, open, addItem, updateQty, removeItem, clear, openCart, closeCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

export function formatINR(n) {
  return `₹${Number(n).toLocaleString("en-IN")}`;
}
